// src/api/axiosConfig.js
// ✅ Refresh token con protezione race condition (coda richieste)
// ✅ Storage keys con namespace roadctrl_*
// ✅ Gestione errori AI quota e upgrade
// ✅ Dev logging

import axios from 'axios';
import toast from 'react-hot-toast';
import { STORAGE_KEYS } from '../utils/constants';
import i18n from '../i18n/i18n';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const axiosConfig = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  },
});

// ==================== REFRESH TOKEN — RACE CONDITION GUARD ====================
// Se più richieste falliscono contemporaneamente con 401, esegue il refresh
// una sola volta e mette in coda le altre

let _isRefreshing = false;
let _refreshQueue = []; // { resolve, reject }[]

const processQueue = (error, token = null) => {
  _refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  _refreshQueue = [];
};

// ==================== REQUEST INTERCEPTOR ====================

axiosConfig.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Cache buster solo su GET
    if (config.method === 'get') {
      config.params = { ...config.params, _t: Date.now() };
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`📤 ${config.method?.toUpperCase()} ${config.url}`, config.data || '');
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ==================== RESPONSE INTERCEPTOR ====================

axiosConfig.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`📥 ${response.status} ${response.config.url}`, response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const data = error.response?.data;

    // ── 401 Token scaduto → refresh ──────────────────────────────────────
    if (status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

      // Nessun refresh token → logout immediato
      if (!refreshToken) {
        _logout();
        return Promise.reject(error);
      }

      // Refresh già in corso → metti in coda
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosConfig(originalRequest);
        }).catch(() => Promise.reject(error));
      }

      originalRequest._retry = true;
      _isRefreshing = true;

      try {
        const res = await axios.post(`${BASE_URL}/auth/refresh-token`, { refreshToken });
        const { token: newToken, refreshToken: newRefresh } = res.data.data;

        localStorage.setItem(STORAGE_KEYS.TOKEN, newToken);
        if (newRefresh) localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefresh);

        axiosConfig.defaults.headers.Authorization = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        processQueue(null, newToken);
        return axiosConfig(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        _logout();
        return Promise.reject(refreshError);
      } finally {
        _isRefreshing = false;
      }
    }

    // ── 403 Upgrade richiesto ─────────────────────────────────────────────
    if (status === 403 && data?.upgradeRequired) {
      toast.error(data.message || i18n.t('errors.upgradeRequired'));
      setTimeout(() => { window.location.href = '/settings?tab=plan'; }, 2000);
      return Promise.reject(error);
    }

    // ── 403 AI — Piano non include AI ────────────────────────────────────
    if (status === 403 && data?.code === 'AI_PLAN_UPGRADE_REQUIRED') {
      toast.error(i18n.t('errors.aiUpgrade'));
      return Promise.reject(error);
    }

    // ── 429 AI — Quota mensile esaurita ──────────────────────────────────
    if (status === 429 && data?.code === 'AI_QUOTA_EXCEEDED') {
      toast.error(i18n.t('ai.quotaExceeded'));
      return Promise.reject(error);
    }

    // ── 403 Generico ──────────────────────────────────────────────────────
    if (status === 403) {
      toast.error(data?.message || i18n.t('errors.forbidden'));
    }

    // ── 429 Generico ──────────────────────────────────────────────────────
    if (status === 429 && data?.code !== 'AI_QUOTA_EXCEEDED') {
      toast.error(i18n.t('errors.tooManyRequests'));
    }

    // ── 503 AI non configurata ────────────────────────────────────────────
    if (status === 503 && data?.code === 'AI_NOT_CONFIGURED') {
      toast.error(i18n.t('errors.aiNotConfigured'));
      return Promise.reject(error);
    }

    // ── 5xx Server error ──────────────────────────────────────────────────
    if (status >= 500) {
      toast.error(i18n.t('errors.server'));
    }

    // ── Network error (nessuna risposta) ──────────────────────────────────
    if (!error.response) {
      toast.error(i18n.t('errors.network'));
    }

    return Promise.reject(error);
  }
);

// ==================== LOGOUT HELPER ====================

const _logout = () => {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
  toast.error(i18n.t('auth.sessionExpired'));
  setTimeout(() => { window.location.href = '/login'; }, 1500);
};

export { _logout as logout };
export default axiosConfig;